/**
 * Shared Geography Picker — tri-mode selector used by every rollout modal
 * (Internal Controls, Risk Assessments, SOP Walkthroughs).
 *
 * Modes:
 *   - "locations": multi-select explicit ORG_locations rows.
 *   - "subtree"  : single root location; server walks parent_location_id tree
 *                  to include every descendant.
 *   - "type"     : single ORG_geography_types row; server expands to every
 *                  location of that type.
 *
 * Emits an opaque GeographySelection object that matches the backend
 * Pydantic schema and goes straight into the rollout POST body.
 */
import React, { useEffect, useState } from 'react';
import FormControl from './FormControl';
import organizationLocationsService from '../../services/organizationLocationsService';
import { geographyTypeService, GeographyType } from '../../services/geographyTypeService';

export type GeographyMode = 'locations' | 'subtree' | 'type';

export interface GeographySelection {
    mode: GeographyMode;
    location_ids?: number[];
    root_location_id?: number;
    geography_type_id?: number;
}

export const isGeographyComplete = (g: GeographySelection): boolean => {
    if (g.mode === 'locations') return !!g.location_ids && g.location_ids.length > 0;
    if (g.mode === 'subtree')   return !!g.root_location_id;
    if (g.mode === 'type')      return !!g.geography_type_id;
    return false;
};

interface Props {
    value: GeographySelection;
    onChange: (v: GeographySelection) => void;
    disabled?: boolean;
    'data-testid'?: string;
}

const GeographyPicker: React.FC<Props> = ({ value, onChange, disabled, 'data-testid': testId }) => {
    const [locations, setLocations] = useState<{ id: number; name: string }[]>([]);
    const [geoTypes, setGeoTypes] = useState<GeographyType[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                // Approved + active locations only — getAll({status:'ACTIVE'}) leaked
                // draft/submitted/rejected locations into these pickers.
                // Both approved + active only — /dropdown filters server-side,
                // so draft/unapproved locations & geo types never appear.
                const [locs, types] = await Promise.all([
                    organizationLocationsService.getDropdown(),
                    geographyTypeService.dropdown().catch(() => []),
                ]);
                if (cancelled) return;
                setLocations(locs || []);
                setGeoTypes(types || []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const setMode = (mode: GeographyMode) => {
        onChange({ mode });
    };

    return (
        <div className="flex flex-col gap-y-3" data-testid={testId}>
            <FormControl.RadioGroup
                name="geography-mode"
                value={value.mode}
                onChange={v => setMode(v as GeographyMode)}
                inline
                noMargin
                options={[
                    { value: 'locations', label: 'Pick locations' },
                    { value: 'subtree',   label: 'One location + nested' },
                    { value: 'type',      label: 'By geography type' },
                ]}
            />

            {loading && <div className="text-xs text-gray-500">Loading locations…</div>}

            {!loading && value.mode === 'locations' && (
                <FormControl.MultiSelect
                    noMargin
                    placeholder="Select locations…"
                    disabled={disabled}
                    value={value.location_ids || []}
                    onChange={ids => onChange({ mode: 'locations', location_ids: (ids as number[]) })}
                    options={locations.map(l => ({ value: l.id, label: l.name }))}
                />
            )}

            {!loading && value.mode === 'subtree' && (
                <FormControl.SearchSelect
                    noMargin
                    placeholder="Select root location…"
                    disabled={disabled}
                    clearable
                    value={value.root_location_id ?? ''}
                    onChange={v => onChange({ mode: 'subtree', root_location_id: v == null || v === '' ? undefined : Number(v) })}
                    options={locations.map(l => ({ value: l.id, label: l.name }))}
                />
            )}

            {!loading && value.mode === 'type' && (
                <FormControl.SearchSelect
                    noMargin
                    placeholder="Select geography type…"
                    disabled={disabled}
                    clearable
                    value={value.geography_type_id ?? ''}
                    onChange={v => onChange({ mode: 'type', geography_type_id: v == null || v === '' ? undefined : Number(v) })}
                    options={geoTypes.map(t => ({ value: t.id, label: t.name }))}
                />
            )}
        </div>
    );
};

export default GeographyPicker;
